import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Box,
    Typography,
    Slider,
    CircularProgress,
    IconButton,
    Paper
} from '@mui/material';
import API_URL from '../config';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const CrossConversionModal = ({ open, onClose, onImport, selectionSize }) => {
    const [sourceFile, setSourceFile] = useState(null);
    const [sourceImg, setSourceImg] = useState(null);
    const [processedImg, setProcessedImg] = useState(null);
    const [processedCells, setProcessedCells] = useState(null);
    const [colorPalette, setColorPalette] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [params, setParams] = useState({
        brightness: 100,
        contrast: 100,
        saturation: 100,
        targetWidth: 5,
        targetHeight: 5,
        targetColor: 16
    });

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSourceFile(file);
            const reader = new FileReader();
            reader.onload = (e) => setSourceImg(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const processImage = async () => {
        if (!sourceFile) return;

        setIsLoading(true);
        const formData = new FormData();
        formData.append('image', sourceFile);
        formData.append('brightness', params.brightness);
        formData.append('contrast', params.contrast);
        formData.append('saturation', params.saturation);
        formData.append('targetWidth', params.targetWidth);
        formData.append('targetHeight', params.targetHeight);
        formData.append('targetColor', params.targetColor);
        
        // Send target grid dimensions if selection size is provided
        if (selectionSize) {
            const targetCols = Math.max(1, Math.round(selectionSize.width / params.targetWidth));
            const targetRows = Math.max(1, Math.round(selectionSize.height / params.targetWidth));
            formData.append('targetCols', targetCols);
            formData.append('targetRows', targetRows);
        }

        try {
            const response = await fetch(`${API_URL}/api/process-image`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                const result = await response.json();
                setProcessedImg(result.processed_image);
                setProcessedCells(result.processed_cells);
                setColorPalette(result.color_palette.map((c, i) => ({
                    color: `rgb(${c[0]},${c[1]},${c[2]})`,
                    color_id: i,
                    NoDraw: i === 0 && c.every(v => v > 240) // Simple transparency heuristic
                })));
            } else {
                console.error('Processing failed');
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!sourceFile) return;
        const timer = setTimeout(() => {
            processImage();
        }, 800);
        return () => clearTimeout(timer);
    }, [params, sourceFile, selectionSize]);

    const handleImport = () => {
        if (processedCells) {
            onImport({
                cells: processedCells,
                colorPalette: colorPalette,
                cols: processedCells[0].length,
                rows: processedCells.length,
                crossSize: params.targetWidth
            });
            onClose();
        }
    };

    const updateParam = (name, value) => {
        setParams(prev => ({ ...prev, [name]: value }));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Image to Crosses Conversion
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={3} sx={{ height: '60vh' }}>
                    {/* Controls Panel */}
                    <Box sx={{ width: '300px', overflowY: 'auto', pr: 2 }}>
                        <Stack spacing={2}>
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                fullWidth
                            >
                                Upload Image
                                <input type="file" hidden accept="image/*" onChange={handleFileUpload} />
                            </Button>

                            <Box>
                                <Typography variant="caption">Brightness ({params.brightness}%)</Typography>
                                <Slider size="small" min={0} max={200} value={params.brightness} onChange={(e, v) => updateParam('brightness', v)} />
                            </Box>
                            <Box>
                                <Typography variant="caption">Contrast ({params.contrast}%)</Typography>
                                <Slider size="small" min={0} max={200} value={params.contrast} onChange={(e, v) => updateParam('contrast', v)} />
                            </Box>
                            <Box>
                                <Typography variant="caption">Saturation ({params.saturation}%)</Typography>
                                <Slider size="small" min={0} max={200} value={params.saturation} onChange={(e, v) => updateParam('saturation', v)} />
                            </Box>
                            <Box>
                                <Typography variant="caption">Cross Size (W: {params.targetWidth}px)</Typography>
                                <Slider size="small" min={2} max={50} value={params.targetWidth} onChange={(e, v) => {
                                    updateParam('targetWidth', v);
                                    updateParam('targetHeight', v);
                                }} />
                            </Box>
                            <Box>
                                <Typography variant="caption">Max Colors ({params.targetColor})</Typography>
                                <Slider size="small" min={2} max={64} value={params.targetColor} onChange={(e, v) => updateParam('targetColor', v)} />
                            </Box>
                        </Stack>
                    </Box>

                    {/* Preview Panel */}
                    <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
                        <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
                            <Typography variant="overline" sx={{ position: 'absolute', top: 5, left: 10 }}>Source</Typography>
                            {sourceImg ? (
                                <img src={sourceImg} alt="Source" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            ) : (
                                <Typography color="text.secondary">No image</Typography>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa' }}>
                            <Typography variant="overline" sx={{ position: 'absolute', top: 5, left: 10 }}>Converted</Typography>
                            {isLoading && (
                                <Box sx={{ position: 'absolute', zIndex: 1, bgcolor: 'rgba(255,255,255,0.7)', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress size={30} />
                                </Box>
                            )}
                            {processedImg ? (
                                <img src={processedImg} alt="Processed" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                            ) : (
                                <Typography color="text.secondary">Waiting for processing</Typography>
                            )}
                        </Box>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleImport} variant="contained" disabled={!processedCells || isLoading}>
                    Import
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CrossConversionModal;
